/* eslint-disable @typescript-eslint/no-empty-function */
import React from 'react';

import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import { User } from '@/models/User';
import { toBase64 } from '@/utils/file';
import axios from 'axios';
import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';

export interface HelpCenterState {
  isModalOpen: boolean;
  isSendingEmail: boolean;
  user?: User;
  error?: string;
}

export interface HelpCenterActions {
  dispatchOpenModal: (timeoutInMS?: number) => void;
  dispatchCloseModal: (timeoutInMS?: number) => void;
  dispatchContactEmail: (
    formData: HelpCenterFormData,
    helpCenterEmail?: string,
  ) => void;
}

export interface HelpCenterContextData {
  state: HelpCenterState;
  actions: HelpCenterActions;
}

export interface HelpCenterFormData {
  nome: string;
  email: string;
  mensagem: string;
  anexos: File[];
}

interface ProviderProps {
  children: React.ReactNode;
}

const initialValue: HelpCenterContextData = {
  state: {
    isModalOpen: false,
    isSendingEmail: false,
    user: undefined,
  },
  actions: {} as HelpCenterActions,
};

const HelpCenterContext = React.createContext<HelpCenterContextData>(
  initialValue,
);

export const HelpCenterProvider: React.FC<ProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const [{ protocoloExecutado }] = useExecucaoContext();

  const [state, setState] = React.useState<HelpCenterState>(initialValue.state);

  const actions: HelpCenterActions = React.useMemo(() => {
    return {
      dispatchOpenModal: (timeoutInMS = 0) => {
        const openModal = () =>
          setState(prev => ({
            ...prev,
            error: undefined,
            isModalOpen: true,
            isSendingEmail: false,
            user: user || undefined,
          }));

        setTimeout(() => openModal(), timeoutInMS);
      },
      dispatchCloseModal: (timeoutInMS = 0) => {
        const closeModal = () =>
          setState(prev => ({ ...prev, isModalOpen: false }));

        setTimeout(() => closeModal(), timeoutInMS);
      },
      dispatchContactEmail: async (formData, helpCenterEmail) => {
        setState(prev => ({ ...prev, error: undefined, isSendingEmail: true }));

        const attachments =
          formData.anexos.length > 0
            ? await Promise.all(formData.anexos.map(anexo => toBase64(anexo)))
            : [];

        try {
          await axios.post(
            'https://wj3j97pilg.execute-api.sa-east-1.amazonaws.com/stable/contacts',
            {
              to: helpCenterEmail,
              name: formData.nome,
              email: formData.email,
              empresa: user?.empresa?.nome,
              protocolo: protocoloExecutado?.codigo || 'N/A',
              execucao: protocoloExecutado?.contexto_integracao?.id || 'N/A',
              message: formData.mensagem,
              attachments,
              contact_type: 'HELP_CENTER_MEDFLOWAPP',
            },
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
            },
          );
        } catch (error) {
          setState(prev => ({
            ...prev,
            error:
              'Erro ao enviar mensagem, tente novamente em alguns instantes.',
          }));
        } finally {
          setState(prev => ({
            ...prev,
            isSendingEmail: false,
            isModalOpen: !!prev.error,
          }));
        }
      },
    };
  }, [protocoloExecutado, user]);

  const value: HelpCenterContextData = React.useMemo(() => {
    return { state, actions };
  }, [state, actions]);

  return (
    <HelpCenterContext.Provider value={value}>
      {children}
    </HelpCenterContext.Provider>
  );
};

export function useHelpCenterContext(): HelpCenterContextData {
  return React.useContext(HelpCenterContext);
}
